import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { buildPatch, cellText, draftOf, fieldErrorsOf, formFields, productErrorKey, type FormField } from "../../lib/productRecords";
import type { ProductRecordDetail } from "../../services/naraDoor";

interface Props {
  record: ProductRecordDetail;
  /** Where the source document opens; null when the workspace has no handle yet. */
  documentHref: string | null;
  busy: boolean;
  /** The last failed save or approve, as the service threw it. */
  error: unknown;
  onSubmit: (patch: ReturnType<typeof buildPatch>) => void;
}

const FIELD_ERRORS = new Set(["required", "type", "enum"]);

/** Review form of one product: each field's AI value beside the current value. Save sends the data, Approve the status (with unsaved edits). */
export function ProductRecordForm({ record, documentHref, busy, error, onSubmit }: Props) {
  const { t } = useTranslation();
  const fields = formFields(record.extraction.record_schema, { ...record.extracted_data, ...record.data });
  const [draft, setDraft] = useState(() => draftOf(record.data, fields));
  const fieldErrors = fieldErrorsOf(error);
  const outdated = record.status === "outdated";
  const labels = { yes: t("common.yes"), no: t("common.no") };
  const set = (key: string, value: string) => setDraft((d) => ({ ...d, [key]: value }));
  const submit = (action: "save" | "approve") => onSubmit(buildPatch(action, fields, draft, record.data));

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit("save");
      }}
    >
      {documentHref && (
        <Link to={documentHref} className="text-sm text-primary hover:underline">
          {record.document.name ?? t("productCatalog.review.openDocument")}
        </Link>
      )}

      <div className="grid grid-cols-[8rem_1fr_1fr] gap-x-4 gap-y-3 items-start text-sm">
        <span />
        <span className="font-medium text-muted-foreground">{t("productCatalog.review.aiValue")}</span>
        <span className="font-medium text-muted-foreground">{t("productCatalog.review.currentValue")}</span>
        {fields.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            ai={cellText(record.extracted_data[field.key], labels)}
            readonlyText={cellText(record.data[field.key], labels)}
            value={draft[field.key] ?? ""}
            rule={fieldErrors[field.key]}
            labels={labels}
            onChange={(value) => set(field.key, value)}
          />
        ))}
      </div>

      {error != null && (
        <p role="alert" className="text-sm text-red-600">
          {t(productErrorKey(error, "productCatalog.errors.saveFailed"))}
        </p>
      )}
      {outdated && <p className="text-sm text-muted-foreground">{t("productCatalog.review.outdatedHint")}</p>}

      <div className="flex gap-2 justify-end">
        <Button type="submit" variant="outline" disabled={busy}>
          {busy ? t("productCatalog.review.saving") : t("common.save")}
        </Button>
        <Button type="button" disabled={busy || outdated} onClick={() => submit("approve")}>
          {t("productCatalog.review.approve")}
        </Button>
      </div>
    </form>
  );
}

function FieldRow({
  field,
  ai,
  readonlyText,
  value,
  rule,
  labels,
  onChange,
}: {
  field: FormField;
  ai: string;
  readonlyText: string;
  value: string;
  rule?: string;
  labels: { yes: string; no: string };
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = `product-field-${field.key}`;
  const control = "w-full px-3 py-1.5 border border-border rounded-lg bg-background";
  return (
    <>
      <label htmlFor={id} className="font-medium pt-1.5">
        {field.key}
      </label>
      <span className="pt-1.5 break-words">{ai}</span>
      <div>
        {field.kind === "readonly" && <span className="block pt-1.5 break-words text-muted-foreground">{readonlyText}</span>}
        {field.kind === "text" && <input id={id} name={field.key} type="text" className={control} value={value} onChange={(e) => onChange(e.target.value)} />}
        {field.kind === "number" && (
          <input id={id} name={field.key} type="number" step="any" className={control} value={value} onChange={(e) => onChange(e.target.value)} />
        )}
        {field.kind === "boolean" && (
          <input id={id} name={field.key} type="checkbox" className="mt-2" checked={value === "true"} onChange={(e) => onChange(String(e.target.checked))} />
        )}
        {field.kind === "select" && (
          <select id={id} name={field.key} className={control} value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="" />
            {field.options?.map((option) => (
              <option key={String(option)} value={String(option)}>
                {cellText(option, labels)}
              </option>
            ))}
          </select>
        )}
        {rule && <p className="mt-1 text-xs text-red-600">{t(`productCatalog.fieldError.${FIELD_ERRORS.has(rule) ? rule : "other"}`)}</p>}
      </div>
    </>
  );
}
